import { Plus } from 'lucide-react'
import { memo, useCallback } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'

import { cn, formatPoints, type BoardColumnView } from '@/features/issue'

import { IssueCard } from './IssueCard'

/**
 * One board column.
 *
 * The column itself is a drop target as well as its cards, so an empty column
 * can still be dropped into — otherwise a board would deadlock the moment a
 * column emptied.
 */
function BoardColumnImpl({
  column,
  onOpenIssue,
  onCreate,
  canWrite,
  activeIssueId,
  hiddenCount,
}: {
  column: BoardColumnView
  onOpenIssue: (ref: string) => void
  onCreate?: (columnId: string) => void
  canWrite: boolean
  activeIssueId: string | null
  hiddenCount: number
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: { type: 'column', columnId: column.id },
  })

  const handleCreate = useCallback(() => onCreate?.(column.id), [onCreate, column.id])

  // A WIP limit is ADVISORY: it is a signal, not a gate. The column reads as
  // full at the limit and as over it beyond, and drag and drop keeps working
  // either way — the server stopped refusing these moves because a refusal
  // nobody can override just gets routed around from the issue panel instead,
  // which is the same write with none of the signal.
  const limit = column.wip_limit ?? null
  const atLimit = limit !== null && column.issues.length >= limit
  const overLimit = limit !== null && column.issues.length > limit

  return (
    <section
      aria-label={column.name}
      className={cn(
        // Columns SHARE the width instead of each taking a fixed 288px.
        //
        // At w-72 shrink-0, six columns needed 1788px and the board scrolled
        // sideways on every laptop. Now they grow to fill the space and shrink
        // to a 9rem floor, so the arithmetic for six is 6 x 144 + 5 x 12 gaps =
        // 924px of board — comfortable on a 1280 screen once the sidebar and
        // padding are taken off, and the horizontal scroll only appears past
        // the point where columns would be too narrow to read.
        //
        // max-w stops the other end: two columns should not become two 600px
        // slabs with a card floating in each.
        'flex min-w-[9rem] max-w-[20rem] flex-1 basis-0 flex-col rounded-card bg-ink-100/60 dark:bg-ink-900/50',
        atLimit && 'ring-1 ring-inset ring-amber-300',
        overLimit && 'ring-red-300',
      )}
    >
      <header className="flex items-center gap-2 px-3 py-2">
        {/* min-w-0 is what makes truncate work here. A flex item defaults to
            min-width:auto, so without it a long column name refuses to shrink
            and pushes the whole column past its floor — which would undo the
            fit-six-across sizing above. */}
        <h2 className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-wide text-ink-600 dark:text-ink-300">
          {column.name}
        </h2>
        <span
          className={cn(
            'shrink-0 rounded px-1 text-[11px] font-medium tabular-nums',
            overLimit
              ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300'
              : atLimit
                ? 'bg-amber-100 text-amber-800'
                : 'text-ink-500',
          )}
          title={
            limit !== null
              ? overLimit
                ? `${column.issues.length} issues, over the WIP limit of ${limit}`
                : `${column.issues.length} of a ${limit} WIP limit — this column is full`
              : undefined
          }
        >
          {column.issues.length}
          {limit !== null ? `/${limit}` : ''}
        </span>
        {atLimit ? (
          <span className="sr-only">
            {overLimit
              ? `over the work-in-progress limit of ${limit}`
              : `at the work-in-progress limit of ${limit}`}
          </span>
        ) : null}
        {column.points > 0 && (
          <span className="shrink-0 text-[11px] text-ink-400" title="Story points in this column">
            {formatPoints(column.points)} pts
          </span>
        )}
        {canWrite && onCreate && (
          <button
            type="button"
            onClick={handleCreate}
            aria-label={`Create an issue in ${column.name}`}
            className="ml-auto rounded p-0.5 text-ink-400 transition hover:bg-ink-200 hover:text-ink-700 dark:hover:bg-ink-800 dark:hover:text-ink-200"
          >
            <Plus className="size-3.5" strokeWidth={2.25} aria-hidden="true" />
          </button>
        )}
      </header>

      <div
        ref={setNodeRef}
        className={cn(
          'min-h-24 flex-1 overflow-y-auto rounded-b-card px-2 pb-2 transition',
          isOver && 'bg-brand-50/70 dark:bg-brand-950/40',
        )}
      >
        <SortableContext
          items={column.issues.map((issue) => issue.id)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="space-y-2">
            {column.issues.map((issue) => (
              <IssueCard
                key={issue.id}
                issue={issue}
                onOpen={onOpenIssue}
                dragDisabled={!canWrite}
                selected={issue.id === activeIssueId}
              />
            ))}
          </ul>
        </SortableContext>

        {column.issues.length === 0 && (
          <p className="px-1 py-6 text-center text-xs text-ink-400">
            {hiddenCount > 0 ? `${hiddenCount} hidden by filters` : 'Nothing here'}
          </p>
        )}
        {column.issues.length > 0 && hiddenCount > 0 && (
          <p className="px-1 pt-2 text-center text-[11px] text-ink-400">
            {hiddenCount} hidden by filters
          </p>
        )}
      </div>
    </section>
  )
}

/* Memoised for the same reason as IssueCard: a drag over one column must not
   re-render the others. */
export const BoardColumn = memo(BoardColumnImpl)
